import { createSlice } from "@reduxjs/toolkit";
import { serverApi } from "src/api/server-api";
import { wordApi } from "src/api/word-api";
import { ReviewMode } from "src/types";

import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  CharacterStyleRecord,
  CharStyleFormattingOrigin,
  ParagraphStyleRecord,
} from "src/services";
import type {
  ClassificationResponse,
  OutlineMode,
  OutlineSortMode,
  ProfileSummary,
  StyleDefinition,
  StyleProfileResponse,
} from "src/types";

export const NOT_IN_PROFILE = "Not in Profile";

export type ReviewState = {
  availableProfiles: ProfileSummary[] | null;
  boundaryIndices: [number, number] | null;
  characterStyleRecords: CharacterStyleRecord[] | null;
  classifications: ClassificationResponse | null;
  divisionText: Record<string, string> | null;
  ignoreUpdateSelection: boolean;
  ignoreParagraphs: boolean;
  outlineMode: OutlineMode;
  outlineSortMode: OutlineSortMode;
  paragraphStyleRecords: ParagraphStyleRecord[] | null;
  paragraphs: Pick<Word.Paragraph, "style" | "uniqueLocalId">[] | null;
  reviewMode: ReviewMode;
  selectedCharacterStyles: string | null;
  selectedCharacterStyleFilter: string | null;
  selectedCharacterOriginFilter: CharStyleFormattingOrigin | null;
  selectedGroupFilter: string | null;
  selectedParagraphs: string[] | null;
  selectedProfile: ProfileSummary | null;
  selectedStyleFilter: string | null;
  styleDefinitionMap: Record<string, StyleDefinition> | null;
  styleProfile: StyleProfileResponse | null;
  trackedChanges: Record<string, number> | null;
};

const initialState: ReviewState = {
  availableProfiles: null,
  boundaryIndices: null,
  characterStyleRecords: null,
  classifications: null,
  divisionText: null,
  ignoreParagraphs: false,
  ignoreUpdateSelection: false,
  outlineMode: "style",
  outlineSortMode: "book_section",
  paragraphStyleRecords: null,
  paragraphs: null,
  reviewMode: ReviewMode.AllParagraphs,
  selectedCharacterStyles: null,
  selectedCharacterStyleFilter: null,
  selectedCharacterOriginFilter: null,
  selectedGroupFilter: null,
  selectedParagraphs: null,
  selectedProfile: null,
  selectedStyleFilter: null,
  styleDefinitionMap: null,
  styleProfile: null,
  trackedChanges: null,
};

const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {
    setBoundaryIndices: (state, action: PayloadAction<ReviewState["boundaryIndices"]>) => {
      state.boundaryIndices = action.payload;
    },
    setIgnoreParagraphs: (state, action: PayloadAction<ReviewState["ignoreParagraphs"]>) => {
      if (state.selectedStyleFilter === "p") {
        state.selectedStyleFilter = null;
      }
      state.ignoreParagraphs = action.payload;
    },
    setIgnoreUpdateSelection: (
      state,
      action: PayloadAction<ReviewState["ignoreUpdateSelection"]>
    ) => {
      state.ignoreUpdateSelection = action.payload;
    },
    setOutlineMode: (state, action: PayloadAction<ReviewState["outlineMode"]>) => {
      state.outlineMode = action.payload;
    },
    setOutlineSortMode: (state, action: PayloadAction<ReviewState["outlineSortMode"]>) => {
      state.outlineSortMode = action.payload;
    },
    setReviewMode: (state, action: PayloadAction<ReviewState["reviewMode"]>) => {
      state.reviewMode = action.payload;
    },
    setSelectedCharacterStyles: (
      state,
      action: PayloadAction<ReviewState["selectedCharacterStyles"]>
    ) => {
      state.selectedCharacterStyles = action.payload;
    },
    setSelectedCharacterStyleFilter: (
      state,
      action: PayloadAction<ReviewState["selectedCharacterStyleFilter"]>
    ) => {
      state.selectedCharacterStyleFilter = action.payload;
      // The origin filter only narrows a style filter, so clearing one clears both.
      if (action.payload === null) {
        state.selectedCharacterOriginFilter = null;
      }
    },
    setSelectedCharacterOriginFilter: (
      state,
      action: PayloadAction<ReviewState["selectedCharacterOriginFilter"]>
    ) => {
      state.selectedCharacterOriginFilter = action.payload;
    },
    setSelectedGroupFilter: (state, action: PayloadAction<ReviewState["selectedGroupFilter"]>) => {
      state.selectedGroupFilter = action.payload;
    },
    setSelectedParagraphs: (state, action: PayloadAction<ReviewState["selectedParagraphs"]>) => {
      state.selectedParagraphs = action.payload;
    },
    setSelectedProfile: (state, action: PayloadAction<string>) => {
      const profile = state.availableProfiles?.find((p) => p.name === action.payload);
      if (profile) {
        state.selectedProfile = profile;
      } else {
        state.selectedProfile = null;
      }
    },
    setSelectedStyleFilter: (state, action: PayloadAction<ReviewState["selectedStyleFilter"]>) => {
      state.selectedStyleFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(wordApi.endpoints.getCharacterStyleRecords.matchFulfilled, (state, action) => {
        state.characterStyleRecords = action.payload;
      })
      .addMatcher(serverApi.endpoints.classifyDocument.matchFulfilled, (state, action) => {
        state.classifications = action.payload;
      })
      .addMatcher(wordApi.endpoints.getDivisionText.matchFulfilled, (state, action) => {
        state.divisionText = action.payload;
      })
      .addMatcher(serverApi.endpoints.getFlattenedStyles.matchFulfilled, (state, action) => {
        if (action.meta.arg.originalArgs === "rhml") return;
        const styleDefinitionMap: Record<string, StyleDefinition> = {};
        for (const styleDefinition of action.payload) {
          styleDefinitionMap[styleDefinition.style] = styleDefinition;
        }
        state.styleDefinitionMap = styleDefinitionMap;
      })
      .addMatcher(serverApi.endpoints.getAvailableProfiles.matchFulfilled, (state, action) => {
        state.availableProfiles = action.payload;
      })
      .addMatcher(wordApi.endpoints.getParagraphs.matchFulfilled, (state, action) => {
        state.paragraphs = action.payload;
      })
      .addMatcher(serverApi.endpoints.getStyleProfile.matchFulfilled, (state, action) => {
        state.styleProfile = action.payload;
      })
      .addMatcher(wordApi.endpoints.getStyleRecords.matchFulfilled, (state, action) => {
        state.paragraphStyleRecords = action.payload;
      })
      .addMatcher(wordApi.endpoints.getTrackedChanges.matchFulfilled, (state, action) => {
        state.trackedChanges = action.payload;
      });
  },
});

export const {
  setBoundaryIndices,
  setIgnoreParagraphs,
  setIgnoreUpdateSelection,
  setOutlineMode,
  setOutlineSortMode,
  setSelectedCharacterStyles,
  setSelectedCharacterStyleFilter,
  setSelectedCharacterOriginFilter,
  setSelectedGroupFilter,
  setSelectedParagraphs,
  setSelectedProfile,
  setReviewMode,
  setSelectedStyleFilter,
} = reviewSlice.actions;

export default reviewSlice.reducer;
